import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Initial / starter projects bundled with the site
const STARTER = [
  {
    src: "/gallery/rim-orange.jpeg",
    title: "Motorradfelge · Custom Orange",
    cat: "Spezial-Lackierung",
  },
  {
    src: "/gallery/yamaha-tank.jpeg",
    title: "Yamaha Tank · Race-Design",
    cat: "Zweikomponenten-Design",
  },
  {
    src: "/gallery/fingerprint-tank.jpeg",
    title: "Motorradtank · Fingerprint",
    cat: "Custom-Grafik & Klarlack",
  },
  {
    src: "/gallery/ducati-red.jpeg",
    title: "Sport-Verkleidung · Ferrari-Rot",
    cat: "Hochglanz-Lackierung",
  },
  {
    src: "/gallery/italia-white.jpeg",
    title: "Sportbike · Tricolore",
    cat: "Komplett-Lackierung",
  },
];

export default function Gallery() {
  const { t } = useI18n(); // reserved for future i18n labels
  const [uploaded, setUploaded] = useState([]);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/gallery`)
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.items || []).map((it) => ({
          src: `${BACKEND}/api${it.url}`, // route via /api/* so the ingress proxies to backend
          title: it.title || "Projekt",
          cat: it.category || "Werkstatt-Projekt",
        }));
        setUploaded(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Uploaded items first, then starter set
  const projects = [...uploaded, ...STARTER];

  const close = () => setActive(-1);
  const prev = () =>
    setActive((i) => (i - 1 + projects.length) % projects.length);
  const next = () => setActive((i) => (i + 1) % projects.length);

  return (
    <section
      id="references"
      data-testid="gallery-references-section"
      className="relative py-16 md:py-20 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#e52620]" />
              <span
                data-testid="references-eyebrow"
                className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono"
              >
                Referenzarbeiten
              </span>
            </div>
            <h2
              data-testid="references-title"
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
            >
              Aus unserer Lackierkabine
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Echte Projekte aus dem MP-Atelier — Spezial-Lackierungen,
              Custom-Designs und Hochglanz-Finishes.
            </p>
          </div>
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-[0.2em]">
            {projects.length} Projekte
          </div>
        </div>

        {/* Uniform masonry-ish grid: every tile = aspect 4:3 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {projects.map((p, i) => (
            <button
              key={p.src + i}
              data-testid={`reference-card-${i}`}
              onClick={() => setActive(i)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 hover:border-[#e52620]/50 transition-all duration-500 text-left"
            >
              <img
                src={p.src}
                alt={p.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-90 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#e52620] font-mono font-bold mb-1 truncate">
                  {p.cat}
                </div>
                <div className="text-white font-bold text-sm tracking-tight leading-tight truncate">
                  {p.title}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active >= 0 && projects[active] && (
        <div
          data-testid="reference-lightbox"
          className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={close}
        >
          <button
            data-testid="reference-lightbox-close"
            onClick={close}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="reference-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="reference-lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={projects[active].src}
              alt={projects[active].title}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="mt-5 text-center">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#e52620] font-mono font-bold">
                {projects[active].cat}
              </div>
              <div className="mt-2 text-xl md:text-2xl font-bold text-white tracking-tight">
                {projects[active].title}
              </div>
              <div className="mt-2 text-xs text-zinc-500 font-mono">
                {active + 1} / {projects.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
